/**
 * My Requests Page
 *
 * Shows end users their submitted support requests with status tracking.
 * This is the user-facing view of their tickets, using simpler terminology
 * than the IT-facing ticket views.
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useTicketRepository } from '../../data/hooks/useTicketRepository';
import type { Ticket } from '../../types/ticket';
import { TicketStatus, Priority } from '../../types/ticket';
import './MyRequestsPage.css';

type RequestFilter = 'all' | 'open' | 'resolved';

interface StatusConfig {
  label: string;
  color: string;
  bgColor: string;
}

function getStatusConfig(status: string): StatusConfig {
  switch (status) {
    case TicketStatus.NEW:
      return { label: 'Submitted', color: '#0078d4', bgColor: 'rgba(0, 120, 212, 0.1)' };
    case TicketStatus.IN_PROGRESS:
      return { label: 'In Progress', color: '#ca5010', bgColor: 'rgba(202, 80, 16, 0.1)' };
    case TicketStatus.WAITING:
      return { label: 'Waiting', color: '#8764b8', bgColor: 'rgba(135, 100, 184, 0.1)' };
    case TicketStatus.RESOLVED:
      return { label: 'Resolved', color: '#107c10', bgColor: 'rgba(16, 124, 16, 0.1)' };
    case TicketStatus.CLOSED:
      return { label: 'Closed', color: '#605e5c', bgColor: 'rgba(96, 94, 92, 0.1)' };
    default:
      return { label: status, color: '#605e5c', bgColor: 'rgba(96, 94, 92, 0.1)' };
  }
}

function getPriorityLabel(priority: Priority): string {
  switch (priority) {
    case Priority.P1:
      return 'Critical';
    case Priority.P2:
      return 'High';
    case Priority.P3:
      return 'Normal';
    case Priority.P4:
      return 'Low';
    default:
      return 'Normal';
  }
}

function formatDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: now.getFullYear() !== new Date(date).getFullYear() ? 'numeric' : undefined,
    }).format(new Date(date));
  }
}

export function MyRequestsPage() {
  const { theme } = useTheme();
  const currentUser = useCurrentUser();
  const ticketRepo = useTicketRepository();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<RequestFilter>('all');

  // Load user's tickets
  useEffect(() => {
    let mounted = true;

    async function loadTickets() {
      try {
        const allTickets = await ticketRepo.getAll();
        // Filter to only show tickets created by or for the current user
        const userTickets = allTickets.filter(
          (ticket) =>
            ticket.requesterId === currentUser.id || ticket.createdBy === currentUser.id
        );
        // Sort by created date, newest first
        userTickets.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        if (mounted) {
          setTickets(userTickets);
        }
      } catch (error) {
        console.error('Failed to load tickets:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadTickets();

    return () => {
      mounted = false;
    };
  }, [ticketRepo, currentUser.id]);

  // Filter tickets
  const filteredTickets = useMemo(() => {
    if (filter === 'all') return tickets;
    if (filter === 'open') {
      return tickets.filter((t) => t.status !== TicketStatus.RESOLVED);
    }
    return tickets.filter((t) => t.status === TicketStatus.RESOLVED);
  }, [tickets, filter]);

  // Stats
  const stats = useMemo(() => {
    const open = tickets.filter((t) => t.status !== TicketStatus.RESOLVED && t.status !== TicketStatus.CLOSED).length;
    const resolved = tickets.filter((t) => t.status === TicketStatus.RESOLVED || t.status === TicketStatus.CLOSED).length;
    const needsResponse = tickets.filter(
      (t) => t.status === TicketStatus.WAITING && t.waitingReason === 'User'
    ).length;
    return { open, resolved, needsResponse, total: tickets.length };
  }, [tickets]);

  const handleNewRequest = () => {
    navigate('/help');
  };

  const handleTicketClick = (ticketId: string) => {
    navigate(`/my-requests/${ticketId}`);
  };

  return (
    <div className="my-requests" data-theme={theme}>
      <div className="my-requests__container">
        {/* Header */}
        <header className="my-requests__header">
          <div className="my-requests__header-content">
            <h1 className="my-requests__title">My Requests</h1>
            <p className="my-requests__subtitle">
              Track the status of your support requests
            </p>
          </div>
          <button
            type="button"
            className="my-requests__new-btn"
            onClick={handleNewRequest}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 4v10M4 9h10" strokeLinecap="round" />
            </svg>
            New Request
          </button>
        </header>

        {/* Stats */}
        <div className="my-requests__stats">
          <div className="my-requests__stat">
            <span className="my-requests__stat-value">{stats.open}</span>
            <span className="my-requests__stat-label">Open</span>
          </div>
          <div className="my-requests__stat">
            <span className="my-requests__stat-value">{stats.resolved}</span>
            <span className="my-requests__stat-label">Resolved</span>
          </div>
          {stats.needsResponse > 0 && (
            <div className="my-requests__stat my-requests__stat--alert">
              <span className="my-requests__stat-value">{stats.needsResponse}</span>
              <span className="my-requests__stat-label">Needs Response</span>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="my-requests__filters">
          <button
            type="button"
            className={`my-requests__filter ${filter === 'all' ? 'my-requests__filter--active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({stats.total})
          </button>
          <button
            type="button"
            className={`my-requests__filter ${filter === 'open' ? 'my-requests__filter--active' : ''}`}
            onClick={() => setFilter('open')}
          >
            Open ({stats.open})
          </button>
          <button
            type="button"
            className={`my-requests__filter ${filter === 'resolved' ? 'my-requests__filter--active' : ''}`}
            onClick={() => setFilter('resolved')}
          >
            Resolved ({stats.resolved})
          </button>
        </div>

        {/* Request List */}
        <div className="my-requests__list">
          {isLoading ? (
            <div className="my-requests__loading">
              <div className="my-requests__spinner" />
              <span>Loading your requests...</span>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="my-requests__empty">
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <rect x="6" y="10" width="36" height="28" rx="2" />
                <path d="M6 18h36M14 26h10M14 32h20" strokeLinecap="round" />
              </svg>
              <h3>
                {filter === 'all'
                  ? 'No requests yet'
                  : filter === 'open'
                    ? 'No open requests'
                    : 'No resolved requests'}
              </h3>
              <p>
                {filter === 'all'
                  ? 'Submit your first request to get help from IT'
                  : filter === 'open'
                    ? 'All your requests have been resolved!'
                    : 'Your resolved requests will appear here'}
              </p>
              {filter === 'all' && (
                <button
                  type="button"
                  className="my-requests__empty-btn"
                  onClick={handleNewRequest}
                >
                  Get Help
                </button>
              )}
            </div>
          ) : (
            filteredTickets.map((ticket) => {
              const statusConfig = getStatusConfig(ticket.status);
              return (
                <button
                  key={ticket.id}
                  type="button"
                  className="my-requests__item"
                  onClick={() => handleTicketClick(ticket.id)}
                  aria-label={`View ticket ${ticket.ticketNumber}`}
                >
                  <div className="my-requests__item-main">
                    <div className="my-requests__item-header">
                      <span className="my-requests__item-number">
                        {ticket.ticketNumber}
                      </span>
                      <span
                        className="my-requests__item-status"
                        style={{
                          color: statusConfig.color,
                          backgroundColor: statusConfig.bgColor,
                        }}
                      >
                        {statusConfig.label}
                      </span>
                    </div>
                    <p className="my-requests__item-description">
                      {ticket.shortDescription}
                    </p>
                    <div className="my-requests__item-meta">
                      <span>{formatDate(ticket.createdAt)}</span>
                      <span className="my-requests__item-dot">·</span>
                      <span>{getPriorityLabel(ticket.priority)} priority</span>
                      <span className="my-requests__item-dot">·</span>
                      <span>{ticket.ticketType}</span>
                    </div>
                  </div>
                  <div className="my-requests__item-actions">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M8 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
